import { ref } from "vue";

import type ITemplateMarkup from "@interfaces/contentManager/ITemplateMarkup";
import type IGetEmbeddedPageContentResponse from "@interfaces/IGetEmbeddedPageContentResponse";
import { defineStore } from "pinia";
import type IFrontendRenderedPageMeta from "../interfaces/site/IFrontendRenderedPageMeta";
import { fetchData } from "../utility/webServices";
import { useAppStore } from "./appStore";
import { resolveQueuedRequestMap } from "./storeHelpers";

export interface IGetPagePageMetaResponse {
  PageMeta: IFrontendRenderedPageMeta;
  MaintenanceBannerMessage?: string;
}

const pageMetaLocalStore = ref<IFrontendRenderedPageMeta[]>([]);
const pages = ref<Map<number, string>>(new Map<number, string>());
const templateMarkup = ref<Map<number, ITemplateMarkup>>(new Map<number, ITemplateMarkup>());
const templateMarkupRequestsInFlight = ref<number[]>([]);
const embeddedPageContent = ref<Map<string, IGetEmbeddedPageContentResponse>>(
  new Map<string, IGetEmbeddedPageContentResponse>()
);
const embeddedPageContentRequestsInFlight = ref<string[]>([]);

//Author-created pages all share one page id, so the slug has to be part of what a cached entry is for.
const embeddedContentCacheKey = (pageId: number, contentPageSlug?: string): string =>
  `${pageId}::${contentPageSlug ?? ""}`;

export const usePageStore = defineStore("pageStore", () => {
  /**
   * Retrieves the embedded content for a specific page by its id.
   * @param pageId The ID of the page for which to retrieve embedded content.
   * @returns A promise that resolves to the embedded page content response or undefined if not found.
   */
  const getPageEmbeddedContentByPageId = async (
    pageId: number,
    contentPageSlug?: string
  ): Promise<IGetEmbeddedPageContentResponse | undefined> => {
    const cacheKey = embeddedContentCacheKey(pageId, contentPageSlug);
    if (embeddedPageContent.value.has(cacheKey)) {
      return embeddedPageContent.value.get(cacheKey);
    }

    if (embeddedPageContentRequestsInFlight.value.includes(cacheKey)) {
      return await resolveQueuedRequestMap(cacheKey, embeddedPageContent.value);
    }

    const embeddedContent = await fetchData<IGetEmbeddedPageContentResponse>(
      "ContentApi/get-embedded-page-content",
      { PageId: pageId, ContentPageSlug: contentPageSlug ?? null }
    );
    if (embeddedContent) {
      embeddedPageContent.value.set(cacheKey, embeddedContent);
    }
    if (embeddedPageContentRequestsInFlight.value.includes(cacheKey)) {
      embeddedPageContentRequestsInFlight.value = embeddedPageContentRequestsInFlight.value.filter(
        (key) => key !== cacheKey
      );
    }

    return embeddedContent ?? undefined;
  };

  /**
   * Retrieves the metadata for a specific page by its base URL.
   * @param pageBaseUrl The base URL of the page for which to retrieve metadata.
   * @returns A promise that resolves to the page metadata or null if not found.
   */
  const getPageMeta = async (pageBaseUrl: string): Promise<IFrontendRenderedPageMeta | null> => {
    //does the store already have the values we are looking for?
    const pm = pageMetaLocalStore.value.filter((x) => x.DefaultUrlRoute === pageBaseUrl);
    if (pm.length === 1) {
      return Promise.resolve(pm[0]);
    }
    const pageMetaResp = await fetchData<IGetPagePageMetaResponse>("ContentApi/get-page-meta", pageBaseUrl);
    if (pageMetaResp) {
      if (!pageMetaLocalStore.value.some((x_1) => x_1.PageId === pageMetaResp.PageMeta.PageId)) {
        pageMetaLocalStore.value.push(pageMetaResp.PageMeta);
      }
      if (pageMetaResp?.MaintenanceBannerMessage ?? "") {
        const appArgs = useAppStore();
        appArgs.setMaintenanceBannerMessage(pageMetaResp?.MaintenanceBannerMessage ?? "");
      }
    }
    return pageMetaResp?.PageMeta ?? null;
  };

  /**
   * Gets a map of all pages with their IDs and URLs.
   * @returns A promise that resolves to a map of all pages with their IDs and URLs.
   */
  const getAllPagesMap = async (): Promise<Map<number, string>> => {
    if (pages.value && pages.value.size > 0) {
      return Promise.resolve(pages.value);
    }
    const pagesMap = await fetchData<Map<number, string>>("ContentApi/get-pages");
    pages.value = pagesMap ?? new Map<number, string>();
    return pages.value;
  };

  /**
   * Retrieves the markup for a specific page template by its ID.
   * @param pageTemplateId The ID of the page template for which to retrieve markup.
   * @returns A promise that resolves to the template markup or undefined if not found.
   */
  const getTemplateMarkup = async (pageTemplateId: number): Promise<ITemplateMarkup | undefined> => {
    if (pageTemplateId <= 0) {
      console.warn("getTemplateMarkup called with invalid pageTemplateId:", pageTemplateId);
      return undefined;
    }

    if (templateMarkup.value.has(pageTemplateId)) {
      return templateMarkup.value.get(pageTemplateId) ?? undefined;
    }

    if (templateMarkupRequestsInFlight.value.includes(pageTemplateId)) {
      return await resolveQueuedRequestMap(pageTemplateId, templateMarkup.value);
    }

    const template = await fetchData<ITemplateMarkup>("ContentApi/get-template-markup", pageTemplateId);
    if (template) {
      templateMarkup.value.set(pageTemplateId, template);
    }

    if (templateMarkupRequestsInFlight.value.includes(pageTemplateId)) {
      templateMarkupRequestsInFlight.value = templateMarkupRequestsInFlight.value.filter(
        (id) => id !== pageTemplateId
      );
    }

    return template ?? undefined;
  };

  return {
    getPageMeta,
    getAllPagesMap,
    getTemplateMarkup,
    getPageEmbeddedContentByPageId,
  };
});
